/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'react-bootstrap';
import moment from 'moment-timezone';
import { FilterMatchMode } from 'primereact/api';
import { showNotification } from '../../../redux/notification';
import refreshIcon from '../../../assets/images/refresh-purple.svg';
import refreshWhiteIcon from '../../../assets/images/refresh-white.svg';
import DateRangeSelector from '../../../components/range-selector/DateRangeSelector';
import AgencyAssignModal from './assign-professionals/AgencyAssignModal';
import {
	useExternalAgencyOpenBundlesQuery,
	useExternalAgencyBundleShiftsQuery
} from '../../../redux/externalAgencyShifts';
import { applyFilters } from '../../../utils/FilterFunctions';
import FiltersSection from './FiltersSection';
import BundlesCountBanner from './BundlesCountBanner';
import {
	shiftTypeTemplate,
	estimatedPayTemplate,
	contractPeriodTemplate,
	facilityTemplate,
	buildFilterOptions,
	startDateTemplate,
	timePeriodTemplate,
	rateTemplate
} from './agency-utils';

export const DEFAULT_FILTER_OPTIONS = {
	facilities: [],
	shiftTypes: [],
	states: []
};

const AgencyBundles = ({ searchTerm }) => {
	const timer = useRef(null);
	const dispatch = useDispatch();
	const [selectedRow, setSelectedRow] = useState(null);
	const [expandedRows, setExpandedRows] = useState(null);
	const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);
	const [filters, setFilters] = useState(DEFAULT_FILTER_OPTIONS);
	const [rangeStart, setRangeStart] = useState(new Date());
	const [rangeEnd, setRangeEnd] = useState(moment(rangeStart).add(13, 'day').toDate());
	const [bundles, setBundles] = useState([]);
	const [showAssignProfessionalModal, setShowAssignProfessional] = useState(null);
	const [latestAssignedBundle, setLatestAssignedBundle] = useState(null);
	const [assignedProfessionalAnimation, setAssignedProfessionalAnimation] = useState(false);
	const [isStartingFetch, setIsStartingFetch] = useState(false);
	const [assignedBundlesShifts, setAssignedBundlesShifts] = useState({});
	const [globalFilter, setGlobalFilter] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });

	const { data: openBundles, refetch: reloadOpenBundles } = useExternalAgencyOpenBundlesQuery(
		{ start: moment(rangeStart).format('YYYY-MM-DD'), end: moment(rangeEnd).format('YYYY-MM-DD') },
		{ refetchOnMountOrArgChange: true }
	);
	const expandedBundlesIds = Object.keys(expandedRows || {});
	const expandedBundleId = expandedBundlesIds?.[0];
	/* should skip bundle shifts re-fetch if no expanded bundle or if bundle was already assigned */
	const skipBundleShiftsReload = !expandedBundlesIds?.length || assignedBundlesShifts[expandedBundleId];
	const { data: bundleShifts, refetch: reloadBundleShifts, isFetching, error } = useExternalAgencyBundleShiftsQuery(
		expandedBundleId,
		{
			skip: skipBundleShiftsReload,
			refetchOnMountOrArgChange: true
		}
	);

	useEffect(() => {
		setRangeEnd(moment(rangeStart).add(13, 'day'));

		return () => clearTimeout(timer.current);
	}, []);

	useEffect(() => {
		const _filter = { ...globalFilter };
		_filter.global.value = searchTerm;
		setGlobalFilter(_filter);
	}, [searchTerm]);

	const handleAssignProfessionalModal = (shift) => {
		setShowAssignProfessional(true);
		setSelectedRow(shift);
	};

	const refreshFeed = useCallback(() => {
		setExpandedRows(null);
		setAssignedBundlesShifts({});
		reloadOpenBundles();
	}, [reloadOpenBundles]);

	const refreshBundle = useCallback(() => {
		setIsStartingFetch(true);
		reloadBundleShifts();
	}, [reloadBundleShifts]);

	useEffect(() => {
		if (isFetching) {
			setIsStartingFetch(false);
		}
	}, [isFetching]);

	useEffect(() => {
		if (openBundles) {
			const options = buildFilterOptions(openBundles, filters);
			setFilterOptions(options);
		}
	}, [openBundles]);

	useEffect(() => {
		setExpandedRows(null);
	}, [rangeStart, rangeEnd]);

	useEffect(() => {
		const _filteredBundles = applyFilters({ items: openBundles || [], filters });
		setBundles(_filteredBundles);
	}, [openBundles, filters]);

	const updateFilters = (updatedOptions, updatedFilters) => {
		setFilterOptions(updatedOptions);
		setFilters(updatedFilters);
	};

	const hasFiltersApplied = filters.facilities?.length || filters.shiftTypes?.length || !!filters.startTimes?.length;

	const handleRowToggle = (_expandedRow) => {
		const _expandedBundleId = Object.keys(_expandedRow)?.[0];
		if (_expandedBundleId && !assignedBundlesShifts[_expandedBundleId]) {
			setIsStartingFetch(true);
		}
		setExpandedRows(_expandedRow);
	};

	/* e.data structure looks like { [row_key]: true, [row_key): true]}  */
	const onRowToggle = (e) => {
		const _newlyExpendedRows = { ...e.data };
		if (Object.keys(e.data).length > 1) {
			delete _newlyExpendedRows[Object.keys(expandedRows)[0]];
		}
		handleRowToggle(_newlyExpendedRows);
	};

	const professionalTemplate = (item) => {
		if (item.caregiverName) {
			return <div className="professional-name">{item.caregiverName}</div>;
		}

		if (item.contract_id === Object.keys(expandedRows || {})?.[0]) {
			return (
				<div className="expand-button" onClick={() => handleRowToggle({})}>
					Hide details
				</div>
			);
		}
		return (
			<div className="expand-button" onClick={() => handleRowToggle({ [item.contract_id]: true })}>
				View details
			</div>
		);
	};

	const rowClass = (rowData) => {
		if (assignedProfessionalAnimation && rowData.contract_id === latestAssignedBundle.contract_id) {
			return 'highlight-assigned-row';
		}
		if (expandedRows && Object.keys(expandedRows)?.[0] === rowData.contract_id) return 'expanded-bundle-row';
		return '';
	};

	const rowExpansionTemplate = (item) => {
		if (!isStartingFetch && !isFetching && error) {
			return (
				<div className="bundle-shifts-error-wrapper">
					<div>
						There was an error trying to view this bundle’s details. Click the button below to refresh the bundle.
					</div>
					<button type="button" className="btn reload-bundle-button" onClick={refreshBundle}>
						<img src={refreshWhiteIcon} alt="Refresh" />
						<div className="button-label">Refresh bundle details</div>
					</button>
				</div>
			);
		}

		return (
			<div className="bundle-shifts-wrapper">
				<DataTable
					value={isStartingFetch || isFetching ? [] : assignedBundlesShifts[item.contract_id] || bundleShifts}
					cellClassName="table-cell"
					loading={isStartingFetch || isFetching}
					emptyMessage="No results found. You can refresh the feed or try a different bundle."
				>
					<Column field="start_time" header="Date" sortable body={startDateTemplate} />
					<Column field="shift_type" header="Shift type" sortable body={shiftTypeTemplate} />
					<Column field="unit" header="Unit" sortable />
					<Column field="start_hour_tz" header="Time" body={timePeriodTemplate} />
					<Column field="estimated_pay" header="Est. pay" sortable body={estimatedPayTemplate} />
					<Column field="rate" header="Rate" sortable body={rateTemplate} />
				</DataTable>
				<div className="assign-button-container">
					{!item.caregiverName && (
						<Button className="assign-button" onClick={() => handleAssignProfessionalModal(item)}>
							Assign
						</Button>
					)}
				</div>
			</div>
		);
	};

	const _showNotification = (type, message) => dispatch(showNotification(type, message));

	const updateBundle = (_bundle) => {
		const newArray = bundles.map((item) => {
			if (item.contract_id === _bundle.contract_id) {
				return {
					...item,
					caregiverName: _bundle.caregiverName
				};
			}
			return item;
		});
		setBundles(newArray);
	};

	const onAssignCompleted = (_bundle) => {
		setShowAssignProfessional(false);
		setSelectedRow(null);
		setLatestAssignedBundle(_bundle);

		if (_bundle.credentialCheckError) {
			return;
		}

		if (_bundle.error) {
			reloadBundleShifts();
			if (_bundle.errorField === 'available_shifts') {
				reloadOpenBundles();
			}
		} else {
			setExpandedRows(null);
			setAssignedProfessionalAnimation(true);
			updateBundle(_bundle);
			setAssignedBundlesShifts({
				...assignedBundlesShifts,
				[_bundle.contract_id]: bundleShifts
			});

			timer.current = setTimeout(() => {
				setAssignedProfessionalAnimation(false);
				setLatestAssignedBundle(null);
			}, 4300);
		}
	};

	return (
		<>
			<div className="toolbar">
				<DateRangeSelector
					rangeStart={rangeStart}
					rangeEnd={rangeEnd}
					setRangeStart={setRangeStart}
					setRangeEnd={setRangeEnd}
					minDate={new Date()}
					proTip="Note: Selected date ranges will also include bundles that start before and after the selected dates, but still have shifts within the selected date range."
				/>
				<button type="button" className="btn refresh-button" onClick={refreshFeed}>
					<img src={refreshIcon} alt="Refresh" />
					<div className="button-label">Refresh Feed</div>
				</button>
				<div className="flex-one" />
				<FiltersSection
					filterOptions={filterOptions}
					filters={filters}
					updateFilters={updateFilters}
					isDisabled={!!globalFilter.global.value}
				/>
			</div>
			<div className="datatable-wrapper">
				<div className="wrapper-container">
					<DataTable
						value={bundles}
						scrollable
						scrollHeight="flex"
						sortMode="multiple"
						selectionMode="single"
						selection={selectedRow}
						dataKey="contract_id"
						tableStyle={{ minWidth: '100rem' }}
						size="small"
						stripedRows
						cellClassName="table-cell"
						rowClassName={rowClass}
						footer={
							bundles?.length ? (
								<BundlesCountBanner bundlesCount={bundles.length} filtersApplied={hasFiltersApplied} />
							) : null
						}
						expandedRows={expandedRows}
						onRowToggle={onRowToggle}
						rowExpansionTemplate={rowExpansionTemplate}
						filters={globalFilter}
					>
						<Column expander style={{ width: '20px', paddingRight: 0 }} />
						<Column
							field="contract_start_time"
							header="Date"
							style={{ width: '14%' }}
							sortable
							body={contractPeriodTemplate}
						/>
						<Column field="shift_type" header="Shift type" style={{ width: '14%' }} sortable body={shiftTypeTemplate} />
						<Column field="shift_count" header="Shifts" style={{ width: '5%', textAlign: 'center' }} sortable />
						<Column field="facility_management_company_name" header="Management" style={{ width: '15%' }} sortable />
						<Column field="facility_name" header="Facility" style={{ width: '15%' }} sortable body={facilityTemplate} />
						<Column field="unit" header="Unit" style={{ width: '11%' }} sortable />
						<Column
							field="estimated_pay"
							header="Est. pay"
							style={{ width: '10%' }}
							sortable
							body={estimatedPayTemplate}
						/>
						<Column field="facility_city" header="City" style={{ width: '11%' }} sortable />
						<Column field="facility_state_code" header="State" style={{ width: '5%' }} sortable />
						<Column field="facility_zip_code" header="ZIP" style={{ width: '5%' }} sortable />
						<Column field="contract_id" header="ID" style={{ width: '6%' }} sortable />
						<Column
							field="professional"
							header="Actions"
							style={{ width: '150px', minWidth: '150px' }}
							alignFrozen="right"
							frozen
							body={professionalTemplate}
						/>
					</DataTable>

					{showAssignProfessionalModal && (
						<AgencyAssignModal
							bundle={selectedRow}
							bundleShifts={bundleShifts}
							onCancel={() => setShowAssignProfessional(false)}
							onComplete={onAssignCompleted}
							showNotification={_showNotification}
							dateTemplate={contractPeriodTemplate}
						/>
					)}
				</div>
			</div>
		</>
	);
};

export default AgencyBundles;
